import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Inbox, Plus, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputVenture } from "@/components/ui/input-venture";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useTickets } from "@/hooks/useTickets";
import { formatDistanceToNow } from "date-fns";
import { PageTour } from "@/components/tour/PageTour";
import { supportSteps } from "@/lib/tour-steps";

type QuickHelpTab = 'getting-started' | 'billing' | 'demos' | 'technical';

const quickHelpContent: Record<QuickHelpTab, { question: string; answer: string }[]> = {
  'getting-started': [
    { question: "How do I view my demos?", answer: "Go to Demo Library from the navigation menu to see all your recorded demos." },
    { question: "How do I update my profile?", answer: "Navigate to Settings > Account to update your profile information." },
  ],
  'billing': [
    { question: "Where can I see my billing?", answer: "Navigate to Billing tab to view payment methods and billing history." },
    { question: "When am I charged?", answer: "Billing occurs every Sunday at 11:59 PM for the previous week's demos." },
  ],
  'demos': [
    { question: "Where are my demo recordings?", answer: "All recordings are available in the Demo Library." },
    { question: "How are demos qualified?", answer: "Demos are qualified based on ICP matching, budget authority, and timeline." },
  ],
  'technical': [
    { question: "I can't log in", answer: "Use your email as username. Contact support for a password reset." },
    { question: "Demo isn't showing up", answer: "Demos appear within 24 hours of completion." },
  ],
};

function QuickHelpItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 hover:bg-surface transition-colors text-left">
        <span className="font-medium">{question}</span>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="px-4 pb-4 text-sm text-muted-foreground">{answer}</div>}
    </div>
  );
}

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'open': return 'warning';
    case 'in_progress': return 'info';
    case 'resolved': return 'success';
    case 'closed': return 'default';
    default: return 'default';
  }
}

function statusLabel(status: string) {
  switch (status) {
    case 'in_progress': return 'In Progress';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

export default function Support() {
  const navigate = useNavigate();
  const { tickets, loading, createTicket } = useTickets();

  const [activeQuickHelpTab, setActiveQuickHelpTab] = useState<QuickHelpTab>('getting-started');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [message, setMessage] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setSubject('');
    setCategory('');
    setPriority('');
    setMessage('');
    setAttachmentFile(null);
  };

  const handleSubmit = async () => {
    if (!subject.trim()) { toast.error('Please enter a subject'); return; }
    if (!category) { toast.error('Please select a category'); return; }
    if (!priority) { toast.error('Please select a priority'); return; }
    if (!message.trim()) { toast.error('Please enter a message'); return; }

    setSubmitting(true);
    try {
      const ticket = await createTicket({
        subject: subject.trim(),
        category,
        priority,
        message: message.trim(),
        attachmentFile: attachmentFile || undefined,
      });
      toast.success('Ticket submitted — your account manager will respond within 24 hours');
      setDialogOpen(false);
      resetForm();
      navigate(`/support/ticket/${ticket.id}`);
    } catch (err) {
      toast.error('Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <PageTour steps={supportSteps} />
    <motion.div className="space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Support</h1>
          <p className="text-muted-foreground">Submit tickets and get help from your account manager</p>
        </div>
        <Button variant="gradient" onClick={() => setDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {/* Ticket List */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>My Tickets</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {loading ? (
            <div className="flex flex-col gap-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-14 rounded-lg bg-muted/30 animate-pulse" />
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Inbox className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">No tickets yet</p>
              <p className="text-xs mt-1">Click "New Ticket" to submit your first support request</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => navigate(`/support/ticket/${ticket.id}`)}
                  className="w-full flex items-center justify-between py-3 px-1 hover:bg-surface/50 transition-colors text-left group"
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="font-medium truncate group-hover:text-secondary transition-colors">{ticket.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Updated {formatDistanceToNow(new Date(ticket.updated_at), { addSuffix: true })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <BadgeVenture variant="default" className="text-xs capitalize">
                      {ticket.category.replace('_', ' ')}
                    </BadgeVenture>
                    <BadgeVenture variant={statusBadgeVariant(ticket.status)} className="text-xs">
                      {statusLabel(ticket.status)}
                    </BadgeVenture>
                  </div>
                </button>
              ))}
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* Quick Help FAQ */}
      <VentureCard>
        <VentureCardHeader><VentureCardTitle>Quick Help</VentureCardTitle></VentureCardHeader>
        <VentureCardContent className="p-0">
          <div className="flex border-b border-border overflow-x-auto">
            {(['getting-started', 'billing', 'demos', 'technical'] as QuickHelpTab[]).map((tab) => (
              <button key={tab} onClick={() => setActiveQuickHelpTab(tab)} className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${activeQuickHelpTab === tab ? 'border-secondary text-secondary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                {tab === 'getting-started' ? 'Getting Started' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          <div>{quickHelpContent[activeQuickHelpTab].map((item, idx) => <QuickHelpItem key={idx} question={item.question} answer={item.answer} />)}</div>
        </VentureCardContent>
      </VentureCard>

      {/* New Ticket Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Submit a Support Ticket</DialogTitle>
            <DialogDescription>Describe your issue and we'll get back to you within 24 hours.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="subject">Subject</Label>
              <InputVenture
                id="subject"
                placeholder="Brief description of your issue..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="integration">Integration</SelectItem>
                    <SelectItem value="demo_issue">Demo Issue</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="message">Message</Label>
              <textarea
                id="message"
                className="w-full min-h-28 rounded-md border border-input bg-surface px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Describe your issue in detail..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors w-fit">
                <Paperclip className="w-4 h-4" />
                {attachmentFile ? attachmentFile.name : 'Attach a file (optional)'}
                <input
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf"
                  onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                />
              </label>
              {attachmentFile && (
                <button onClick={() => setAttachmentFile(null)} className="text-xs text-destructive mt-1 hover:underline">
                  Remove attachment
                </button>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
            <Button variant="gradient" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
    </>
  );
}
